<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Tenant/Landlord Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('TABLES.RentRequests.tenant_landlord_name')"
                v-model.trim="filterOptions.tenant_landlord_name"
              />
              <!-- End:: Tenant/Landlord Name Input -->

              <!-- Start:: Status Select -->
              <base-select-input
                col="3"
                :optionsList="statusOptions"
                :placeholder="$t('TABLES.RentRequests.status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Select -->

              <!-- Start:: Date From Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_from')"
                v-model="filterOptions.from"
              />
              <!-- End:: Date From Input -->

              <!-- Start:: Date To Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date_to')"
                v-model="filterOptions.to"
              />
              <!-- End:: Date To Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TABLES.RentRequests.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Tenant Name -->
        <template v-slot:[`item.tenant_name`]="{ item }">
          <span class="text-danger" v-if="!item.tenant || !item.tenant.name">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.tenant.name }} </span>
        </template>
        <!-- End:: Tenant Name -->

        <!-- Start:: Tenant Mobile -->
        <template v-slot:[`item.tenant_mobile`]="{ item }">
          <span class="text-danger" v-if="!item.tenant || !item.tenant.mobile">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.tenant.mobile }} </span>
        </template>
        <!-- End:: Tenant Mobile -->

        <!-- Start:: Landlord Name -->
        <template v-slot:[`item.landlord_name`]="{ item }">
          <span
            class="text-danger"
            v-if="!item.ad || !item.ad.user || !item.ad.user.user"
          >
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.ad.user.user.name }} </span>
        </template>
        <!-- End:: Landlord Name -->

        <!-- Start:: Landlord Mobile -->
        <template v-slot:[`item.landlord_mobile`]="{ item }">
          <span
            class="text-danger"
            v-if="!item.ad || !item.ad.user || !item.ad.user.user"
          >
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.ad.user.user.mobile }} </span>
        </template>
        <!-- End:: Landlord Mobile -->

        <!-- Start:: Ad Name -->
        <template v-slot:[`item.ad_name`]="{ item }">
          <span class="text-danger" v-if="!item.ad || !item.ad.name">
            {{ $t("TABLES.noData") }}
          </span>
          <span v-else> {{ item.ad.name }} </span>
        </template>
        <!-- End:: Ad Name -->

        <!-- Start:: Request Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <span> {{ item.created_at || "-" }} </span>
        </template>
        <!-- End:: Request Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            :class="{
              'text-warning': item.status === 'pending',
              'text-success': item.status === 'accepted',
              'text-danger': item.status === 'rejected',
            }"
          >
            {{ item._status || "-" }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Show Button -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item.id)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <div class="pagination_container">
        <v-pagination
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          color="#404b3e"
        ></v-pagination>
      </div>
      <!--  =========== End:: Pagination =========== -->
    </main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "AllRentRequests",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        tenant_landlord_name: null,
        status: null,
        from: null,
        to: null,
      },
      statusOptions: [
        {
          id: "pending",
          name: this.$t("TABLES.RentRequests.pending"),
        },
        {
          id: "accepted",
          name: this.$t("TABLES.RentRequests.accepted"),
        },
        {
          id: "rejected",
          name: this.$t("TABLES.RentRequests.rejected"),
        },
      ],
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.RentRequests.serial"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.tenant_name"),
          value: "tenant.user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.tenant_mobile"),
          value: "tenant.user.mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.landlord_name"),
          value: "landlord.user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.landlord_mobile"),
          value: "landlord.user.mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.ad_name"),
          value: "ad_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.request_date"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.RentRequests.actions"),
          value: "actions",
          align: "center",
          width: "120",
          sortable: false,
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data
    };
  },

  methods: {
    // Start:: Vuex Actions
    ...mapActions({
      updateAppLoadingStatus: "LoadingModule/updateAppLoadingStatus",
    }),
    // End:: Vuex Actions

    // Start:: Get All Data
    async getAllData() {
      this.loading = true;
      this.updateAppLoadingStatus(true);

      try {
        let res = await this.$axios({
          method: "GET",
          url: "rent-requests",
          params: {
            page: this.paginations.current_page,
            tenant_landlord_name: this.filterOptions.tenant_landlord_name,
            status: this.filterOptions.status?.id,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.updateAppLoadingStatus(false);
        this.tableRows = res.data.data.data;
        this.paginations.current_page = res.data.data.meta.current_page;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        this.updateAppLoadingStatus(false);
        console.log(error.response.data.message);
      }
    },
    // End:: Get All Data

    // Start:: Submit Filter Form
    submitFilterForm() {
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Submit Filter Form

    // Start:: Reset Filter
    resetFilter() {
      this.filterOptions.tenant_landlord_name = null;
      this.filterOptions.status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      this.paginations.current_page = 1;
      this.getAllData();
    },
    // End:: Reset Filter

    // Start:: Update Router Query Param
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      this.paginations.current_page = pagenationValue;
      this.getAllData();
    },
    // End:: Update Router Query Param

    // Start:: Show Item
    showItem(id) {
      this.$router.push({ path: `/rent-requests/show/${id}` });
    },
    // End:: Show Item
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = Number(this.$route.query.page);
    }
    this.getAllData();
    // End:: Fire Methods
  },
};
</script>
